import React from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity, View } from 'react-native';
import { IconSvg } from 'src/assets';
import { EstimateResponseProps } from 'src/common/api/response';
import useNativeSafeInsets from 'src/common/hooks/useNativeSafeInsets';
import { amountFormat } from 'src/common/utils/AmountFormat';
import { Container, LabelValueRow, PaymentBreakdown, PrimaryButton, RegularText, Row, SizedBox } from 'src/components';
import { AppColors } from 'src/themes/Colors';
import { metrics } from 'src/themes/Metrics';
import styled from 'styled-components/native';

interface Props {
  text: string;
  backgroundColor?: string;
  onPress: () => void;
  disabled?: boolean;
}

const Styled = {
  FooterContainer: styled.View`
    border-top-width: 1px;
    border-color: ${AppColors.border};
    box-shadow: 0px 4px 10px rgba(199, 199, 199, 0.25);
    padding: ${metrics.large}px;
    background-color: ${AppColors.white};
    padding-top: ${metrics.medium}px;
    border-top-left-radius: ${metrics.borderRadius}px;
    border-top-right-radius: ${metrics.borderRadius}px;
  `,
};

export const FooterButton = ({ text, onPress, backgroundColor = 'white', disabled }: Props) => {
  const insets = useNativeSafeInsets();

  return (
    <Container
      backgroundColor={backgroundColor}
      style={{
        padding: metrics.medium,
        paddingBottom: metrics.medium + insets.bottom,
        borderTopWidth: 1,
        borderColor: AppColors.border,
        shadowColor: AppColors.gray,
        shadowOffset: {
          width: 0,
          height: -1,
        },
        shadowRadius: 5,
        shadowOpacity: 0.15,
      }}>
      <PrimaryButton style={{ borderRadius: 100 }} disabled={disabled} text={text} onPress={onPress} />
    </Container>
  );
};

export const SeatFooter = ({ seatQTY, onPress, disabled }: { seatQTY: any; onPress(): any; disabled?: boolean }) => {
  const { t } = useTranslation();
  const insets = useNativeSafeInsets();

  return (
    <Styled.FooterContainer style={{ paddingBottom: metrics.large + insets.bottom }}>
      <Row style={{ alignItems: 'center', alignSelf: 'center' }} marginBottom={metrics.medium}>
        <RegularText fontSize="large">{t('selectedSeat')} </RegularText>
        <RegularText fontSize="larger" fontWeight="bold">
          {' '}
          ({seatQTY})
        </RegularText>
      </Row>
      <PrimaryButton style={{ borderRadius: 50 }} disabled={disabled} text="select" onPress={onPress} />
    </Styled.FooterContainer>
  );
};

export const PassengerFooter = ({ total, onCheckoutPress }: { total?: number | null; onCheckoutPress(): any }) => {
  const insets = useNativeSafeInsets();

  return (
    <Styled.FooterContainer style={{ paddingBottom: metrics.large + insets.bottom }}>
      <LabelValueRow
        fontWeightValue="bold"
        fontSizeLabel="large"
        fontSizeValue="larger"
        label="total"
        value={amountFormat(total)}
      />
      <SizedBox />
      <PrimaryButton style={{ borderRadius: 50 }} text="checkout" onPress={onCheckoutPress} />
    </Styled.FooterContainer>
  );
};

export const ConfirmPaymentFooter = ({
  onPayNowPress,
  disabled,
  estimation,
  text = 'payNow',
}: {
  onPayNowPress(): any;
  disabled: boolean;
  text?: string;
  estimation?: EstimateResponseProps;
}) => {
  const [isDetail, setDetail] = React.useState<boolean>(false);
  const insets = useNativeSafeInsets();

  return (
    <Styled.FooterContainer style={{ backgroundColor: AppColors.white, paddingBottom: metrics.large + insets.bottom }}>
      <TouchableOpacity activeOpacity={0.8} onPress={() => setDetail(prev => !prev)}>
        {isDetail ? <PaymentBreakdown estimation={estimation} /> : <View />}
        <Row style={{ alignItems: 'center', justifyContent: 'space-between' }} marginBottom={metrics.large}>
          <Row middle>
            <RegularText fontSize="large" textLang="total" />
            <SizedBox />
            {!isDetail ? <IconSvg.ArrowDown fill={AppColors.gray} /> : <IconSvg.ArrowUp fill={AppColors.gray} />}
          </Row>
          <RegularText fontSize="huge" fontWeight="bold">
            {amountFormat(estimation?.total)}
          </RegularText>
        </Row>
      </TouchableOpacity>
      <PrimaryButton style={{ borderRadius: 50 }} disabled={disabled} text={text} onPress={onPayNowPress} />
    </Styled.FooterContainer>
  );
};
